import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Redirect, useParams } from "react-router";
import useText from "../../hooks/useText";

interface Api {
  createDemographics: (demographicData: any) => Promise<any>;
  getNextQuestion: () => Promise<any>;
}
interface LetsMeetProps {
  api: Api;
  redirectLink?: string;
}

const usIndex = 177;

const Spacer = () => <Box sx={{ marginBottom: 5 }} />;

const LetsMeet: React.FC<LetsMeetProps> = ({ api, redirectLink }) => {
  const [loading, setLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [demoData, setDemoData] = useState({
    gender: "",
    age: "",
    employmentStatus: "",
    live: "",
    industry: "",
    companySize: "",
    jobLevel: "",
    feelings: "",
    satisfy: "",
    happiness: "",
    acceptPrivacyPolicy: false,
    acceptAcknowledgement: false,
    jobFunction: "",
    jobTime: "",
    roleTime: "",
    tag: useParams<{ tag?: string }>().tag,
  });
  const text = useText()?.letsMeet;
  const lang = useText()?.lang ?? 1;

  const demoOptions: {
    label: string;
    helper: string;
    options: string[];
    key: keyof typeof demoData;
  }[] = (
    [
      "gender",
      "age",
      "employmentStatus",
      "live",
      "industry",
      "companySize",
      "jobLevel",
      "feelings",
      "satisfy",
      "happiness",
      "jobFunction",
      "jobTime",
      "roleTime",
    ] satisfies (keyof typeof demoData)[]
  )
    .filter((k) => text.demographics[k])
    .map((k) => ({
      ...text.demographics[k],
      key: k,
    }));
  const onContinue = async () => {
    if (loading) return;
    setLoading(true);
    const r = await api.createDemographics(demoData);
    setLoading(false);
    setRedirect(redirectLink && r.success);
  };

  useEffect(() => {
    const hasAnswered = async () => {
      const questionCheck = await api.getNextQuestion();
      if (questionCheck.questionsAnswered > 0) setRedirect(false); //todo
    };
    hasAnswered();
  }, []);

  const isTurkish = parseInt(lang, 10) === 4;

  if (redirect && redirectLink) return <Redirect to={redirectLink} />;

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" color="primary">
            {text.introduction.title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            <Typography variant="body1" gutterBottom>
              {text.introduction.overview}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {text.introduction.instructions}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {text.introduction.time}
            </Typography>
          </div>
        </AccordionDetails>
      </Accordion>
      <Spacer />
      <Card
        raised
        sx={{
          background: (theme) =>
            `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: (theme) => theme.palette.primary.contrastText,
        }}
      >
        <CardContent>
          <Typography variant="body1">{text.introduction.privacy}</Typography>
        </CardContent>
      </Card>
      <Spacer />
      <Card raised>
        <CardContent>
          <Typography variant="h6" color="primary" gutterBottom>
            {text.demographics.welcome}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {text.demographics.research}
          </Typography>
          <Grid container spacing={2}>
            {demoOptions.map((d) => (
              <Grid key={d.key} item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={d.label}
                  helperText={d.helper}
                  select
                  value={demoData[d.key]}
                  onChange={(e) =>
                    setDemoData((v) => ({ ...v, [d.key]: e.target.value }))
                  }
                >
                  {d.key === "live" && lang === 1 && (
                    <MenuItem value={usIndex + 1}>
                      {d.options[usIndex]}
                    </MenuItem>
                  )}
                  {d.key === "industry"
                    ? d.options
                        .map((label, value) => ({ label, value }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map(({ label, value }) => (
                          <MenuItem key={d.key + "_" + label} value={value + 1}>
                            {label}
                          </MenuItem>
                        ))
                    : d.key === "live" && lang !== 1
                    ? [...d.options.entries()]
                        .sort((a, b) => a[1].localeCompare(b[1]))
                        .map(([value, label]) => (
                          <MenuItem key={d.key + "_" + label} value={value + 1}>
                            {label}
                          </MenuItem>
                        ))
                    : d.options.map((label, index) => (
                        <MenuItem key={d.key + "_" + label} value={index + 1}>
                          {label}
                        </MenuItem>
                      ))}
                </TextField>
              </Grid>
            ))}
            {/* if lang == tur and country == turkiye then ask for city */}
            {demoOptions[3].options.indexOf("Türkiye") + 1 ==
              parseInt(demoData.live, 10) &&
              isTurkish && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={text.demographics.city ?? "Şehir"}
                    helperText={
                      text.demographics.cityHelper ??
                      "Hangi şehirde yaşıyorsun?"
                    }
                    onChange={(e: any) => {
                      const { value } = e.target;
                      setDemoData((v) => ({ ...v, city: value ?? "" }));
                    }}
                  >
                    <input />
                  </TextField>
                </Grid>
              )}
          </Grid>
          <FormControlLabel
            label={
              <span>
                {text.demographics.accept1}{" "}
                <Link
                  target="_blank"
                  href={
                    isTurkish
                      ? "https://hrthinks.me/kisisel-verilerin-korunmasi-kanunu"
                      : "/privacy-policy.pdf"
                  }
                >
                  {text.demographics.accept2}
                </Link>
              </span>
            }
            control={
              <Checkbox
                checked={demoData.acceptPrivacyPolicy}
                onChange={() =>
                  setDemoData((v) => ({
                    ...v,
                    acceptPrivacyPolicy: !v.acceptPrivacyPolicy,
                  }))
                }
              />
            }
          />
          {isTurkish && (
            <FormControlLabel
              label={
                <span>
                  Açık Rıza Beyanı’nı okudum, onaylıyorum.{" "}
                  <Link
                    target="_blank"
                    href="https://hrthinks.me/kisisel-verilerin-korunmasi-kanunu"
                  >
                    {text.demographics.accept2}
                  </Link>
                </span>
              }
              control={
                <Checkbox
                  checked={demoData.acceptAcknowledgement}
                  onChange={() =>
                    setDemoData((v) => ({
                      ...v,
                      acceptAcknowledgement: !v.acceptAcknowledgement,
                    }))
                  }
                />
              }
            />
          )}
        </CardContent>
        <CardActions>
          <Button
            startIcon={loading ? <CircularProgress size={28} /> : undefined}
            onClick={onContinue}
            disabled={Object.keys(demoData).some((k) => {
              console.log(
                optionalFields.has(k)
                  ? false
                  : k === "acceptAcknowledgement" && !isTurkish
                  ? false
                  : !demoData[k as keyof typeof demoData],
                k
              );
              if (optionalFields.has(k)) return false;
              if (k === "acceptAcknowledgement" && !isTurkish) return false;
              return !demoData[k as keyof typeof demoData];
            })}
            variant="contained"
            color="secondary"
          >
            {text.demographics.continue}
          </Button>
        </CardActions>
      </Card>
    </>
  );
};

const optionalFields = new Set([
  "city",
  "tag",
  "jobFunction",
  "jobTime",
  "roleTime",
]);
export default LetsMeet;
